import api from '../../lib/api';
import { Pagination } from '../Pagination';
import StatusBadge from '../StatusBadge';
import { Button } from '../ui/button';
import { CardContent } from '../ui/card';
import { id } from 'date-fns/locale';
import { Eye, Package, Pencil, Printer, Send, Trash2, CopyPlus } from 'lucide-react';
import { toast } from 'sonner';

export function POTable({ SortIcon, canManagePO, currentPage, fetchPOs, handleRevise, handleSort, handleSubmit, openConfirmDialog, openDeleteDialog, openEdit, openPartialDialog, pembulatan, pos, selectedPO, setDpAmount, setDpPO, setPartialItems, setPrintPO, setReasonTarget, setSelectedPO, setShowDPDialog, setShowDetail, setShowPrintOptions, setShowSubmitToAP, setSubmitBiayaAngkut, setSubmitPembulatan, setSubmitUsesVA, setSubmitVAData, setTanggalTerima, sortField, totalCount, totalPages, user }) {
  return (
    <CardContent>
      {pos.length > 0 ? (
        <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-sm font-semibold">PO Number</th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'po_date' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('po_date')}
                >
                  <div className="flex items-center gap-1">
                    Tanggal <SortIcon field="po_date" />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'supplier_name' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('supplier_name')}
                >
                  <div className="flex items-center gap-1">
                    Supplier <SortIcon field="supplier_name" />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'company_name' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('company_name')}
                >
                  <div className="flex items-center gap-1">
                    Perusahaan <SortIcon field="company_name" />
                  </div>
                </th>
                <th className="px-4 py-3 text-sm font-semibold">Total</th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'status' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status <SortIcon field="status" />
                  </div>
                </th>
                <th className="px-4 py-3 text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pos.map((po) => (
                <tr key={po.id} className="border-b" data-testid={`po-row-${po.id}`}>
                  <td className="px-4 py-3 font-mono text-sm font-semibold">
                    {po.po_number}
                    {['purchasing', 'admin'].includes(user?.role) && po.created_by_name && (
                      <div className="text-[10px] font-sans font-normal text-slate-500" data-testid={`po-creator-${po.id}`}>{po.created_by_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm" style={sortField === 'po_date' ? { backgroundColor: '#ffee8b33' } : {}}>{new Date(po.po_date).toLocaleDateString('id-ID')}</td>
                  <td className="px-4 py-3" style={sortField === 'supplier_name' ? { backgroundColor: '#ffee8b33' } : {}}>{po.supplier_name}</td>
                  <td className="px-4 py-3" style={sortField === 'company_name' ? { backgroundColor: '#ffee8b33' } : {}}>{po.company_name || '-'}</td>
                  <td className="px-4 py-3 font-mono">Rp {po.total_amount.toLocaleString('id-ID')}</td>
                  <td className="px-4 py-3" style={sortField === 'status' ? { backgroundColor: '#ffee8b33' } : {}}>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={po.status} />
                      {po.uses_va && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold" title="Virtual Account">VA</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedPO(po); setShowDetail(true); }}
                        data-testid={`view-po-${po.id}`}
                        title="Lihat Detail"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setPrintPO(po); setShowPrintOptions(true); }}
                        data-testid={`print-po-${po.id}`}
                        title="Print"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      {po.status === 'draft' && canManagePO(po) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(po)}
                            data-testid={`edit-po-${po.id}`}
                            title="Edit PO"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSubmit(po.id)}
                            data-testid={`submit-po-${po.id}`}
                            title="Submit PO"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {/* DP Button for Purchasing - submitted POs without existing DP */}
                      {['submitted', 'diterima', 'diterima_adj', 'diterima_partial'].includes(po.status) && ['purchasing', 'admin'].includes(user?.role) && !po.has_dp && (
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white text-xs px-2"
                          onClick={() => { setDpPO(po); setDpAmount(''); setShowDPDialog(true); }}
                          data-testid={`create-dp-${po.id}`}
                          title="Buat Down Payment"
                        >
                          DP
                        </Button>
                      )}
                      {po.has_dp && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold" title={`DP: Rp ${(po.dp_amount || 0).toLocaleString('id-ID')}`}>DP</span>
                      )}
                      {/* Gudang Actions - For submitted and partial POs only (NOT for diterima/diterima_adj) */}
                      {(po.status === 'submitted' || po.status === 'diterima_partial') && ['staff_gudang', 'admin'].includes(user?.role) && (
                        <div className="flex gap-1 flex-wrap">
                          {/* Partial button - only for partial delivery type */}
                          {po.jenis_pengiriman === 'partial' && (
                            <Button
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-2"
                              onClick={() => openPartialDialog(po)}
                              data-testid={`confirm-partial-${po.id}`}
                              title="Partial Delivery"
                            >
                              <Package className="h-3 w-3 mr-1" />
                              Partial
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2"
                            onClick={() => openConfirmDialog(po, 'diterima')}
                            data-testid={`confirm-sesuai-${po.id}`}
                            title="Sesuai PO"
                          >
                            Sesuai
                          </Button>
                          <Button
                            size="sm"
                            className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-2"
                            onClick={() => openConfirmDialog(po, 'diterima_adj')}
                            data-testid={`confirm-tidak-sesuai-${po.id}`}
                            title="Tidak Sesuai PO"
                          >
                            Tidak Sesuai
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs px-2"
                            onClick={() => openConfirmDialog(po, 'reject')}
                            data-testid={`confirm-reject-${po.id}`}
                            title="Reject PO"
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                      {/* Submit to AP - For confirmed POs */}
                      {['diterima', 'diterima_adj', 'diterima_partial'].includes(po.status) && ['staff_clerk', 'admin'].includes(user?.role) && !po.submitted_to_invoice && (
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-2"
                          onClick={async () => {
                            try {
                              // Fetch remaining items from API to avoid re-submitting fully invoiced items
                              const response = await api.get(`/purchase-orders/${po.id}/remaining-items`);
                              const remainingData = response.data;
                              if (remainingData.all_invoiced) {
                                toast.info('Semua item pada PO ini sudah disubmit ke invoice');
                                return;
                              }
                              // Nothing invoiceable yet, but goods are still on the way:
                              // point the user at the receive flow instead of claiming
                              // the PO is already done.
                              if ((remainingData.submittable_count ?? 0) === 0 && (remainingData.unreceived_count ?? 0) > 0) {
                                toast.info('Belum ada item yang bisa di-invoice — item tersisa belum diterima gudang. Terima barang lewat tombol Partial terlebih dahulu.');
                                return;
                              }
                              setSelectedPO(po);
                              // Map API rows into dialog state. Preserve line_id, only
                              // auto-select rows that still have remaining qty to invoice.
                              const items = (remainingData.items || []).map((item, idx) => ({
                                ...item,
                                index: idx,
                                received: !item.fully_invoiced && !item.belum_diterima,
                                received_qty: item.fully_invoiced
                                  ? (item.received_qty || item.original_qty || item.quantity)
                                  : (item.remaining_qty || item.quantity),
                              }));
                              setPartialItems(items);
                              setTanggalTerima(po.tanggal_terima ? new Date(po.tanggal_terima) : new Date());
                              // Auto-set VA state from PO
                              const poUsesVA = po.uses_va || false;
                              setSubmitUsesVA(poUsesVA);
                              setSubmitVAData(null);
                              // Initialize pembulatan from PO, biaya_angkut defaults to remaining allocation
                              setSubmitPembulatan(po.pembulatan || 0);
                              setSubmitBiayaAngkut(remainingData.biaya_angkut_sisa || 0);
                              // Attach remaining biaya info to selectedPO so the dialog can show it
                              setSelectedPO({
                                ...po,
                                biaya_angkut_po: remainingData.biaya_angkut_po,
                                biaya_angkut_terinvoice: remainingData.biaya_angkut_terinvoice,
                                biaya_angkut_sisa: remainingData.biaya_angkut_sisa,
                              });
                              if (poUsesVA && po.supplier_id && po.company_id) {
                                try {
                                  const vaRes = await api.get(`/virtual-accounts/lookup?supplier_id=${po.supplier_id}&company_id=${po.company_id}`);
                                  setSubmitVAData(vaRes.data);
                                } catch { /* VA not found, no problem */ }
                              }
                              setShowSubmitToAP(true);
                            } catch (error) {
                              const detail = error?.response?.data?.detail;
                              toast.error(detail || 'Gagal mengambil data item tersisa');
                            }
                          }}
                          data-testid={`submit-invoice-${po.id}`}
                          title="Submit Invoice"
                        >
                          Submit Invoice
                        </Button>
                      )}
                      {/* Batalkan / Revisi / Tutup PO - pembuat atau admin */}
                      {['draft', 'submitted'].includes(po.status) && canManagePO(po) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-400 text-slate-700 text-xs px-2"
                          onClick={() => setReasonTarget({ type: 'cancel', po })}
                          data-testid={`cancel-po-${po.id}`}
                          title="Batalkan PO"
                        >
                          Batalkan
                        </Button>
                      )}
                      {['reject', 'cancelled'].includes(po.status) && canManagePO(po) && (
                        po.revised_to_po_id ? (
                          <span className="text-xs text-slate-500 self-center" data-testid={`revised-to-${po.id}`}>Direvisi → {po.revised_to_po_number}</span>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-sky-600 hover:bg-sky-700 text-white text-xs px-2"
                            onClick={() => handleRevise(po)}
                            data-testid={`revise-po-${po.id}`}
                            title="Buat Draft revisi dari PO ini"
                          >
                            <CopyPlus className="h-3 w-3 mr-1" />Revisi
                          </Button>
                        )
                      )}
                      {po.status === 'diterima_partial' && canManagePO(po) && (
                        <Button
                          size="sm"
                          className="bg-slate-700 hover:bg-slate-800 text-white text-xs px-2"
                          onClick={() => setReasonTarget({ type: 'close', po })}
                          data-testid={`close-po-${po.id}`}
                          title="Tutup PO partial (sisa tidak dikirim)"
                        >
                          Tutup PO
                        </Button>
                      )}
                      {/* Admin Delete Button */}
                      {user?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openDeleteDialog(po)}
                          data-testid={`delete-po-${po.id}`}
                          title="Hapus PO"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={currentPage} totalPages={totalPages} total={totalCount} onPageChange={(p) => fetchPOs(p)} />
        </>
      ) : (
        <p className="text-slate-500 text-center py-8">Belum ada purchase order</p>
      )}
    </CardContent>
  );
}
